import DOMPurify from 'dompurify'
import { getDownloadURL, ref } from 'firebase/storage'
import { storage } from './firebase'
import type { PagePointer } from './types'

export interface Heading {
  id: string
  text: string
  level: 2 | 3
}

export interface RenderedPage {
  html: string
  headings: Heading[]
}

export class StaffOnlyError extends Error {
  constructor() {
    super('This page is internal. Ask an admin for staff access.')
    this.name = 'StaffOnlyError'
  }
}

/** Firebase Storage denies unauthorized reads with this code. */
const isPermissionDenied = (e: unknown) =>
  typeof e === 'object' &&
  e !== null &&
  'code' in e &&
  (e as { code: string }).code === 'storage/unauthorized'

async function fetchHosting(url: string, signal?: AbortSignal): Promise<string> {
  const res = await fetch(url, { signal })
  if (res.status === 401 || res.status === 403) throw new StaffOnlyError()
  if (!res.ok) throw new Error(`Could not load the page (HTTP ${res.status}).`)
  return res.text()
}

/**
 * The backend sends either a bare object path (`public/a/b/index.html`) or a full
 * media URL (`https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded>?alt=media`).
 * Both must collapse to an object path: it is what `ref()` takes, and what relative
 * image `src`s resolve against.
 */
export function storageObjectPath(location: string): string {
  if (!/^https?:/i.test(location)) return location.replace(/^\/+/, '')

  const encoded = new URL(location).pathname.split('/o/')[1]
  if (!encoded) throw new Error(`Unrecognized Storage location: ${location}`)
  return decodeURIComponent(encoded)
}

async function fetchStorage(location: string, signal?: AbortSignal): Promise<string> {
  let url: string
  try {
    // Go through getDownloadURL rather than fetching the media URL directly: it
    // attaches the caller's credentials, so Storage rules gate `internal/**` on
    // the `role` claim here, before the body is ever fetched.
    url = await getDownloadURL(ref(storage, storageObjectPath(location)))
  } catch (e) {
    if (isPermissionDenied(e)) throw new StaffOnlyError()
    throw e
  }
  const res = await fetch(url, { signal })
  if (!res.ok) throw new Error(`Could not load the page (HTTP ${res.status}).`)
  return res.text()
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-') || 'section'

const isAbsolute = (src: string) => /^(https?:|data:|blob:|\/\/)/i.test(src)

/**
 * Rewrite in-page asset URLs so they resolve from wherever the doc actually lives.
 * Public pages resolve against their Hosting URL; internal pages sit next to
 * their images in Storage, which needs a signed download URL per object.
 */
async function resolveAssets(root: Element, page: PagePointer): Promise<void> {
  const images = [...root.querySelectorAll('img')].filter((img) => {
    const src = img.getAttribute('src')
    return !!src && !isAbsolute(src)
  })

  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute('src')!
      try {
        if (page.source === 'hosting') {
          img.setAttribute('src', new URL(src, page.location).href)
        } else {
          const objectPath = storageObjectPath(page.location)
          const dir = objectPath.slice(0, objectPath.lastIndexOf('/') + 1)
          // Collapse `./` and `../` against the object's directory.
          const resolved = new URL(src, `https://x/${dir}`).pathname.slice(1)
          img.setAttribute('src', await getDownloadURL(ref(storage, resolved)))
        }
      } catch {
        // A broken image should not blank the whole page.
        img.removeAttribute('src')
        img.setAttribute('alt', img.getAttribute('alt') ?? 'Image unavailable')
      }
      img.setAttribute('loading', 'lazy')
    }),
  )
}

/**
 * Fetch a rendered doc body, sanitize it, and hand back HTML plus the heading
 * outline for the "on this page" rail.
 */
export async function loadPage(page: PagePointer, signal?: AbortSignal): Promise<RenderedPage> {
  const raw =
    page.source === 'hosting'
      ? await fetchHosting(page.location, signal)
      : await fetchStorage(page.location, signal)

  const doc = new DOMParser().parseFromString(raw, 'text/html')
  // Published pages are full documents; the article lives in <main> when present.
  const root = doc.querySelector('main') ?? doc.body

  await resolveAssets(root, page)

  // Published pages open with an <h1> carrying the doc title, which DocPage already
  // renders from metadata alongside the tags and vote controls. Keeping it would
  // print the title twice.
  root.querySelectorAll('h1').forEach((h) => h.remove())

  const headings: Heading[] = []
  const seen = new Set<string>()
  root.querySelectorAll('h2, h3').forEach((h) => {
    const text = h.textContent?.trim()
    if (!text) return

    let id = h.id || slugify(text)
    // Duplicate slugs would make the outline jump to the wrong section.
    let n = 2
    while (seen.has(id)) id = `${slugify(text)}-${n++}`
    seen.add(id)
    h.id = id

    headings.push({ id, text, level: h.tagName === 'H2' ? 2 : 3 })
  })

  // Wide tables get their own scroll container so the page never scrolls sideways.
  root.querySelectorAll('table').forEach((table) => {
    if (table.parentElement?.classList.contains('table-scroll')) return
    const wrap = doc.createElement('div')
    wrap.className = 'table-scroll'
    table.replaceWith(wrap)
    wrap.appendChild(table)
  })

  const html = sanitize(root.innerHTML)
  return { html, headings }
}

/** Video embeds we accept. Anything else framed into the page is stripped. */
const EMBED_HOSTS = new Set([
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'youtube-nocookie.com',
  'player.vimeo.com',
])

let hookInstalled = false

/**
 * An <iframe> hands a third party a window inside our page, so it is allowed only
 * for known video hosts and only over https. Everything else — an ad frame, a
 * phishing form, a same-origin frame that could reach our Storage tokens — is dropped.
 */
function installEmbedHook() {
  if (hookInstalled) return
  hookInstalled = true

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return

    const src = (node as Element).getAttribute?.('src') ?? ''
    let ok = false
    try {
      const url = new URL(src, location.href)
      ok = url.protocol === 'https:' && EMBED_HOSTS.has(url.host)
    } catch {
      ok = false
    }
    if (!ok) node.parentNode?.removeChild(node)
  })
}

function sanitize(dirty: string): string {
  installEmbedHook()

  return DOMPurify.sanitize(dirty, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'target',
      'data-pagefind-filter',
      'id',
      'allow',
      'allowfullscreen',
      'frameborder',
      'loading',
      'title',
    ],
    FORBID_TAGS: ['style', 'form', 'input', 'button'],
  })
}
